import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import DataSection from './Datasection';

const UpcomingCoursesSection = ({ showNotification }) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    course_id: '',
    display_order: 0,
    active_status: true,
    image: null
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [formLoading, setFormLoading] = useState(false);
  const [courses, setCourses] = useState([]);

  const columns = [
    { key: 'id', header: 'ID' },
    { key: 'imagePreview', header: 'Image' },
    { key: 'courseTitle', header: 'Course' },
    { key: 'activeStatus', header: 'Status' },
    { key: 'display_order', header: 'Order' }
  ];

  useEffect(() => {
    fetchData();
    fetchCourses();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('https://apissc.sainikschoolcadet.com/api/upcoming-courses');
      setData(response.data);
    } catch (error) {
      console.error('Error fetching upcoming courses:', error);
      showNotification('Failed to load upcoming courses', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get('https://apissc.sainikschoolcadet.com/api/Contents');
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
      showNotification('Failed to load courses', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      course_id: '',
      display_order: 0,
      active_status: true,
      image: null
    });
    setImagePreview(null);
    setErrors({});
    setSelectedItem(null);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      course_id: item.course_id || '',
      display_order: item.display_order || 0,
      active_status: item.active === true || item.active === 1 || item.activeStatus === 'Active',
      image: null
    });

    if (item.imagePreview) {
      setImagePreview(item.imagePreview);
    }
    
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this upcoming course?')) {
      try {
        await axios.delete(`https://apissc.sainikschoolcadet.com/api/upcoming-courses/${id}`);
        setData(prevData => prevData.filter(item => item.id !== id));
        showNotification('Upcoming course deleted successfully');
      } catch (error) {
        console.error('Error deleting upcoming course:', error);
        showNotification('Failed to delete course', 'error');
      }
    }
  };

  const handleAdd = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    setFormData(prev => {
      const updatedFormData = { 
        ...prev, 
        [name]: type === 'file' 
          ? files[0] 
          : type === 'checkbox' 
            ? checked 
            : name === 'display_order' 
              ? parseInt(value, 10) || 0 
              : value 
      };
      return updatedFormData;
    });

    // Handle image preview for file inputs
    if (type === 'file') {
      const file = files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreview(e.target.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.course_id) {
      newErrors.course_id = 'Course is required';
    }
    
    if (formData.display_order < 0) {
      newErrors.display_order = 'Display order must be a positive number';
    }
    
    if (!selectedItem && !formData.image) {
      newErrors.image = 'Please select an image';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    setFormLoading(true);
    
    try {
      let imagePath = null;
      
      // First, handle image upload if there's a new image
      if (formData.image) {
        const imageFormData = new FormData();
        imageFormData.append('image', formData.image);
        imageFormData.append('title', `Upcoming Course ${formData.course_id ? '- ' + formData.course_id : ''}`);
        imageFormData.append('tag', 'upcoming-course');
        
        try {
          const imageUploadResponse = await axios.post(
            'https://apissc.sainikschoolcadet.com/api/images',
            imageFormData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          
          if (imageUploadResponse.data && imageUploadResponse.data.path) {
            imagePath = imageUploadResponse.data.path;
          } else {
            throw new Error('Image upload response did not contain a path');
          }
        } catch (imageError) {
          console.error('Error uploading image:', imageError);
          setErrors(prev => ({ ...prev, image: 'Failed to upload image. Please try again.' }));
          setFormLoading(false);
          return;
        }
      }
      
      // Now create/update the upcoming course with the image path
      const submitData = {
        course_id: formData.course_id,
        display_order: formData.display_order,
        active: formData.active_status ? true : false
      };
      
      // Add image path if we have one from upload or keep existing if editing
      if (imagePath) {
        submitData.image_path = imagePath;
      }
      
      let response;
      
      if (selectedItem) {
        // Update existing item
        response = await axios.put(
          `https://apissc.sainikschoolcadet.com/api/upcoming-courses/${selectedItem.id}`, 
          submitData
        );
      } else {
        // Create new item
        response = await axios.post(
          'https://apissc.sainikschoolcadet.com/api/upcoming-courses',
          submitData
        );
      }
      
      if (response.data) {
        showNotification(selectedItem ? 'Upcoming course updated successfully' : 'Upcoming course added successfully');
        setIsModalOpen(false);
        fetchData();
      }
    } catch (error) {
      console.error('Error saving upcoming course:', error);
      if (error.response && error.response.data && error.response.data.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: 'Failed to save. Please try again.' });
      }
      showNotification('Failed to save upcoming course', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  // Modal component
  const FormModal = ({ isOpen, onClose, children }) => {
    if (!isOpen) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
          {children}
        </div>
      </div>
    );
  };

  return (
    <>
      <DataSection
        title="Upcoming Courses"
        data={data.map(item => ({
          ...item,
          courseTitle: courses.find(course => course.content_id === item.course_id)?.title || 'Unknown'
        }))}
        columns={columns}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAdd={handleAdd}
        isLoading={isLoading}
        emptyMessage="No upcoming courses found"
      />
      
      <FormModal isOpen={isModalOpen} onClose={closeModal}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <h2 className="text-xl font-bold mb-4">
            {selectedItem ? 'Edit Upcoming Course' : 'Add New Upcoming Course'}
          </h2>
          
          <div className="form-group">
            <label htmlFor="course_id" className="block mb-1 font-medium">
              Course *
            </label>
            <select
              id="course_id"
              name="course_id"
              value={formData.course_id}
              onChange={handleChange}
              className={`w-full p-2 border rounded ${errors.course_id ? 'border-red-500' : 'border-gray-300'}`}
            >
              <option value="">Select a course</option>
              {courses.map(course => (
                <option key={course.content_id} value={course.content_id}>
                  {course.title}
                </option>
              ))}
            </select>
            {errors.course_id && (
              <p className="text-red-500 text-sm mt-1">{errors.course_id}</p>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="display_order" className="block mb-1 font-medium">
              Display Order
            </label>
            <input
              type="number"
              id="display_order"
              name="display_order"
              value={formData.display_order}
              onChange={handleChange}
              className={`w-full p-2 border rounded ${errors.display_order ? 'border-red-500' : 'border-gray-300'}`}
              min="0"
            />
            {errors.display_order && (
              <p className="text-red-500 text-sm mt-1">{errors.display_order}</p>
            )}
          </div>
          
          <div className="form-group">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                name="active_status"
                checked={formData.active_status}
                onChange={handleChange}
                className="form-checkbox h-5 w-5"
              />
              <span>Active</span>
            </label>
          </div>
          
          <div className="form-group">
            <label htmlFor="image" className="block mb-1 font-medium">
              Image {!selectedItem && '*'}
            </label>
            <input
              type="file"
              id="image"
              name="image"
              onChange={handleChange}
              className={`w-full p-2 border rounded ${errors.image ? 'border-red-500' : 'border-gray-300'}`}
              accept="image/*"
            />
            {errors.image && (
              <p className="text-red-500 text-sm mt-1">{errors.image}</p>
            )}
            
            {imagePreview && (
              <div className="mt-2">
                <p className="text-sm font-medium mb-1">Preview:</p>
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  className="w-40 h-auto object-cover border rounded"
                />
              </div>
            )}
          </div>
          
          {errors.general && (
            <div className="bg-red-100 text-red-700 p-3 rounded">
              {errors.general}
            </div>
          )}
          
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              disabled={formLoading}
            >
              Cancel
            </button>
            
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              disabled={formLoading}
            >
              {formLoading ? (
                <span>Saving...</span>
              ) : (
                <span>{selectedItem ? 'Update' : 'Save'}</span>
              )}
            </button>
          </div>
        </form>
      </FormModal>
    </>
  );
};

export default UpcomingCoursesSection;